import {ILaudoAuditoria, ILaudoCapitulo} from '../interfaces/ILaudoAuditoria'

// DEVN3-521 — regras da revisão do laudo (tela /auditoria/revisao e barra de progressão).
// Cada capítulo é aceito (entra no PDF) ou recusado (fica fora); a revisão termina quando não
// sobra nenhum pendente. Capítulo com falha de geração aparece, mas não conta nem bloqueia.

export type EstadoRevisao = 'GERANDO' | 'FALHA' | 'ACEITO' | 'RECUSADO' | 'PENDENTE'

export function estadoRevisao(c: ILaudoCapitulo): EstadoRevisao {
  if (c.statusCapitulo === 'PENDENTE') return 'GERANDO'
  if (c.statusCapitulo === 'ERRO') return 'FALHA'
  if (c.statusCapitulo === 'REJEITADO') return 'RECUSADO'
  if (c.dataAprovacao) return 'ACEITO'
  return 'PENDENTE'
}

const geracaoInicialConcluida = (laudo: ILaudoAuditoria) =>
  laudo.status === 'PARCIAL' || laudo.status === 'CONCLUIDO'

// Capítulos que aparecem na revisão, na ordem do laudo. Regra sem apontamento nunca aparece;
// capítulo aguardando geração só aparece quando é uma regeneração (durante a geração inicial
// ainda não se sabe quais regras terão apontamento).
export function capitulosDaRevisao(laudo: ILaudoAuditoria): ILaudoCapitulo[] {
  return laudo.capitulos
    .filter(
      (c) =>
        c.statusCapitulo !== 'SEM_APONTAMENTO' &&
        (c.statusCapitulo !== 'PENDENTE' || geracaoInicialConcluida(laudo))
    )
    .slice()
    .sort((a, b) => a.ordem - b.ordem)
}

export interface ProgressoRevisao {
  total: number
  aceitos: number
  recusados: number
  pendentes: number
  falhas: number
  pct: number
}

export function progressoRevisao(laudo: ILaudoAuditoria): ProgressoRevisao {
  const total = laudo.totalCapitulos
  const aceitos = laudo.capitulosAprovados
  const recusados = laudo.capitulosRecusados ?? 0
  const revisados = Math.min(total, aceitos + recusados)
  return {
    total,
    aceitos,
    recusados,
    pendentes: total - revisados,
    falhas: laudo.capitulos.filter((c) => c.statusCapitulo === 'ERRO').length,
    pct: total > 0 ? Math.round((revisados / total) * 100) : 100,
  }
}

// Próximo capítulo ainda por revisar depois de `aposId` (varredura circular). Sem `aposId`,
// devolve o primeiro pendente.
export function proximoPendente(
  capitulos: ILaudoCapitulo[],
  aposId?: number
): ILaudoCapitulo | undefined {
  const inicio = aposId === undefined ? -1 : capitulos.findIndex((c) => c.id === aposId)
  for (let i = 1; i <= capitulos.length; i++) {
    const c = capitulos[(inicio + i) % capitulos.length]
    if (c.id !== aposId && estadoRevisao(c) === 'PENDENTE') return c
  }
  return undefined
}

// Há algo rodando em background (geração inicial, regeneração de capítulo ou export) — a tela
// precisa continuar consultando o laudo.
export function laudoEmAndamento(laudo: ILaudoAuditoria): boolean {
  return (
    laudo.status === 'GERANDO' ||
    laudo.arquivoRetornoProcessando ||
    (geracaoInicialConcluida(laudo) && laudo.capitulos.some((c) => c.statusCapitulo === 'PENDENTE'))
  )
}
